import React, {useEffect, useMemo, useState} from 'react';
import {ChevronDownIcon, ChevronUpIcon} from '@heroicons/react/24/outline';
import {Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow} from '../components/ui/table';
import {Badge} from '../components/ui/badge';
import {FilterSelect} from '../components/ui/FilterSelect';
import {SearchInput} from '../components/ui/SearchInput';
import Pagination from '../components/ui/pagination';
import {
    AuditEntry, AuditFilters, AuditQuery, fetchAudit, fetchAuditFilters,
} from '../api/audit';

const PAGE_SIZE = 25;

const TYPE_OPTIONS = [
    {value: '', label: 'All types'},
    {value: 'blueprint', label: 'Blueprints'},
    {value: 'site_list', label: 'Site lists'},
    {value: 'scheduled_job', label: 'Scheduled jobs'},
    {value: 'user', label: 'Users'},
    {value: 'company', label: 'Companies'},
    {value: 'brief', label: 'Briefs'},
    {value: 'equativ_taxonomy', label: 'Equativ taxonomies'},
    {value: 'setting', label: 'Settings'},
];
const TYPE_LABELS: Record<string, string> = Object.fromEntries(
    TYPE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label.replace(/s$/, '')]),
);

const DATE_OPTIONS = [
    {value: '0', label: 'Today'},
    {value: '7', label: 'Last 7 days'},
    {value: '30', label: 'Last 30 days'},
    {value: '90', label: 'Last 90 days'},
    {value: 'all', label: 'All time'},
];

const FIELD_LABELS: Record<string, string> = {
    name: 'Name', prompt: 'Prompt', countries: 'Countries', categories: 'Categories',
};
const fieldLabel = (k: string) => FIELD_LABELS[k] || k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ');

const ACTION_BADGE: Record<string, {label: string; variant: 'safe' | 'default' | 'risk'}> = {
    created: {label: 'Created', variant: 'safe'},
    edited: {label: 'Edited', variant: 'default'},
    deleted: {label: 'Deleted', variant: 'risk'},
};

const initials = (name: string) =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function fromDate(range: string): string | undefined {
    if (range === 'all') return undefined;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - Number(range));
    return d.toISOString();
}

type Op = {t: 'same' | 'del' | 'ins'; text: string};

/** Word-level LCS diff; whitespace is kept as part of tokens. */
export function wordDiff(before: string, after: string): Op[] {
    const a = before.match(/\S+\s*/g) || [];
    const b = after.match(/\S+\s*/g) || [];
    const key = (s: string) => s.trim();
    const dp: number[][] = Array.from({length: a.length + 1}, () => new Array(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            dp[i][j] = key(a[i]) === key(b[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }
    const ops: Op[] = [];
    let i = 0, j = 0;
    while (i < a.length && j < b.length) {
        if (key(a[i]) === key(b[j])) { ops.push({t: 'same', text: b[j]}); i++; j++; }
        else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push({t: 'del', text: a[i++]}); }
        else { ops.push({t: 'ins', text: b[j++]}); }
    }
    while (i < a.length) ops.push({t: 'del', text: a[i++]});
    while (j < b.length) ops.push({t: 'ins', text: b[j++]});
    return ops;
}

const delCls = 'rounded bg-risk-bg px-0.5 text-risk line-through';
const insCls = 'rounded bg-safe-bg px-0.5 text-safe no-underline';

const asText = (v: unknown) => (v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v));

const FieldDiff: React.FC<{before: unknown; after: unknown}> = ({before, after}) => {
    if (Array.isArray(before) || Array.isArray(after)) {
        const b = (Array.isArray(before) ? before : []).map(String);
        const a = (Array.isArray(after) ? after : []).map(String);
        const removed = new Set(b.filter((x) => !a.includes(x)));
        const added = new Set(a.filter((x) => !b.includes(x)));
        const join = (items: string[], mark: Set<string>, cls: string, Tag: 'del' | 'ins') => items.length === 0 ? '—' :
            items.map((x, i) => (
                <React.Fragment key={x}>
                    {i > 0 && ', '}
                    {mark.has(x) ? <Tag className={cls}>{x}</Tag> : x}
                </React.Fragment>
            ));
        return (
            <>
                <div>{join(b, removed, delCls, 'del')}</div>
                <div>{join(a, added, insCls, 'ins')}</div>
            </>
        );
    }
    const ops = wordDiff(asText(before), asText(after));
    return (
        <>
            <div>{ops.filter((o) => o.t !== 'ins').map((o, i) =>
                o.t === 'del' ? <del key={i} className={delCls}>{o.text.trimEnd()}{' '}</del> : <span key={i}>{o.text}</span>)}</div>
            <div>{ops.filter((o) => o.t !== 'del').map((o, i) =>
                o.t === 'ins' ? <ins key={i} className={insCls}>{o.text.trimEnd()}{' '}</ins> : <span key={i}>{o.text}</span>)}</div>
        </>
    );
};

const formatWhen = (iso: string) => {
    const d = new Date(iso);
    const time = d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', hour12: false});
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
    if (d.toDateString() === yest.toDateString()) return `Yesterday, ${time}`;
    return `${d.toLocaleDateString([], {month: 'short', day: 'numeric', year: 'numeric'})}, ${time}`;
};

const Actor: React.FC<{e: AuditEntry}> = ({e}) => {
    if (e.actorType === 'ai_rewrite') {
        return (
            <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-warn-bg text-[11px] font-bold text-warn">AI</div>
                <div>
                    <div className="font-bold text-ink">AI rewrite</div>
                    {e.actorName && <div className="text-xs text-ink-muted">from feedback by {e.actorName}</div>}
                </div>
            </div>
        );
    }
    if (e.actorType === 'system' || !e.actorName) {
        return <div className="font-bold text-ink">System</div>;
    }
    return (
        <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-accent text-[11px] font-bold text-white">{initials(e.actorName)}</div>
            <div>
                <div className="font-bold text-ink">{e.actorName}</div>
                {e.actorRole && <div className="text-xs text-ink-muted">{e.actorRole}</div>}
            </div>
        </div>
    );
};

const AuditPage: React.FC = () => {
    const [entityType, setEntityType] = useState('');
    const [blueprint, setBlueprint] = useState('');
    const [actor, setActor] = useState('');
    const [company, setCompany] = useState('');
    const [range, setRange] = useState('30');
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<AuditEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<AuditFilters>({actors: [], companies: [], blueprints: []});
    const [expanded, setExpanded] = useState<string | null>(null);

    useEffect(() => {
        fetchAuditFilters().then(setFilters).catch(() => { /* filters are optional */ });
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        let cancelled = false;
        const params: AuditQuery = {page, limit: PAGE_SIZE};
        if (entityType) params.entityType = entityType;
        if (blueprint) { params.entityType = 'blueprint'; params.entityId = blueprint; }
        if (actor) params.actorUserId = actor;
        if (company) params.companyId = company;
        const from = fromDate(range);
        if (from) params.from = from;
        if (debounced) params.search = debounced;
        setLoading(true);
        fetchAudit(params)
            .then((r) => { if (!cancelled) { setItems(r.items); setTotal(r.total); setError(null); } })
            .catch(() => { if (!cancelled) setError('Failed to load audit log'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [entityType, blueprint, actor, company, range, debounced, page]);

    const reset = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); setExpanded(null); };

    const blueprintOptions = useMemo(() => [{value: '', label: 'All blueprints'},
        ...filters.blueprints.map((b) => ({value: b.id, label: b.name || b.id}))], [filters]);
    const actorOptions = useMemo(() => [{value: '', label: 'Changed by: Anyone'},
        ...filters.actors.map((a) => ({value: a.id, label: a.name || a.email || a.id}))], [filters]);
    const companyOptions = useMemo(() => [{value: '', label: 'All companies'},
        ...filters.companies.map((c) => ({value: c.id, label: c.name || c.id}))], [filters]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const pagination = {
        page, limit: PAGE_SIZE, totalCount: total, totalPages,
        hasNextPage: page < totalPages, hasPreviousPage: page > 1,
        nextPage: page < totalPages ? page + 1 : null, previousPage: page > 1 ? page - 1 : null,
    };

    return (
        <div className="flex flex-col gap-4" data-testid="audit-page">
            <div className="flex flex-wrap items-center gap-3">
                <SearchInput
                    className="min-w-[240px]"
                    placeholder="Search name…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    data-testid="audit-search"
                />
                <FilterSelect data-testid="audit-filter-type" value={entityType} onChange={reset(setEntityType)} options={TYPE_OPTIONS}/>
                <FilterSelect data-testid="audit-filter-blueprint" searchable value={blueprint} onChange={reset(setBlueprint)} options={blueprintOptions}/>
                <FilterSelect data-testid="audit-filter-actor" searchable value={actor} onChange={reset(setActor)} options={actorOptions}/>
                <FilterSelect data-testid="audit-filter-company" searchable value={company} onChange={reset(setCompany)} options={companyOptions}/>
                <FilterSelect data-testid="audit-filter-range" value={range} onChange={reset(setRange)} options={DATE_OPTIONS}/>
            </div>

            {error && <div className="rounded-md bg-risk-bg p-4 text-sm text-risk">{error}</div>}

            <div className="flex flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
                <Table>
                    <TableHead>
                        <TableRow className="hover:bg-transparent">
                            <TableHeaderCell className="w-[150px]">When</TableHeaderCell>
                            <TableHeaderCell>Item</TableHeaderCell>
                            <TableHeaderCell>Changed by</TableHeaderCell>
                            <TableHeaderCell>Change</TableHeaderCell>
                            <TableHeaderCell className="w-10"><span className="sr-only">Expand</span></TableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((e) => {
                            const fields = e.changes ? Object.keys(e.changes) : [];
                            const expandable = e.entityType === 'blueprint' && fields.length > 0;
                            const open = expanded === e.id;
                            const badge = e.actorType === 'ai_rewrite'
                                ? {label: 'AI rewrite', variant: 'warn' as const}
                                : e.actorType === 'system'
                                    ? {label: 'System', variant: 'secondary' as const}
                                    : ACTION_BADGE[e.action];
                            return (
                                <React.Fragment key={e.id}>
                                    <TableRow
                                        data-testid={`audit-row-${e.id}`}
                                        className={`${expandable ? 'cursor-pointer' : ''} ${open ? 'bg-ghost-bg' : ''}`}
                                        onClick={expandable ? () => setExpanded(open ? null : e.id) : undefined}
                                    >
                                        <TableCell className="align-top text-ink">{formatWhen(e.createdAt)}</TableCell>
                                        <TableCell className="align-top">
                                            <div className="font-bold">{e.entityName || e.entityId}</div>
                                            <div className="text-xs text-ink-muted">
                                                {[e.entityType !== 'blueprint' ? TYPE_LABELS[e.entityType] : null, e.companyName]
                                                    .filter(Boolean).join(' · ')}
                                            </div>
                                        </TableCell>
                                        <TableCell className="align-top"><Actor e={e}/></TableCell>
                                        <TableCell className="align-top">
                                            <Badge variant={badge.variant}>{badge.label}</Badge>
                                            {e.entityType === 'blueprint' && fields.length > 0 && (
                                                <span className="ml-1.5 text-[13px] text-ink-muted">{fields.map(fieldLabel).join(' · ')}</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="align-top text-ink-muted">
                                            {expandable && (open ? <ChevronUpIcon className="h-5 w-5"/> : <ChevronDownIcon className="h-5 w-5"/>)}
                                        </TableCell>
                                    </TableRow>
                                    {open && e.changes && (
                                        <TableRow className="bg-ghost-bg hover:bg-ghost-bg">
                                            <TableCell colSpan={5} className="pt-0">
                                                <div className="rounded-xl border border-line bg-surface px-4 pb-3 pt-1.5" data-testid={`audit-diff-${e.id}`}>
                                                    <div className="grid grid-cols-[120px_1fr_1fr] gap-4 border-b border-line py-2.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                                                        <div/><div>Before</div><div>After</div>
                                                    </div>
                                                    {fields.map((k) => (
                                                        <div key={k} className="grid grid-cols-[120px_1fr_1fr] gap-4 border-b border-line py-2.5 text-[13px] leading-relaxed last:border-0">
                                                            <div className="pt-0.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">{fieldLabel(k)}</div>
                                                            <FieldDiff before={e.changes![k].before} after={e.changes![k].after}/>
                                                        </div>
                                                    ))}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {!loading && items.length === 0 && (
                            <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={5} className="py-10 text-center text-ink-muted">No audit events match these filters.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <div className="border-t border-line bg-ghost-bg px-4 py-2.5">
                    <Pagination pagination={pagination} onPageChange={(p) => { setPage(p); setExpanded(null); }}/>
                </div>
            </div>
        </div>
    );
};

export default AuditPage;
